'use client';

/**
 * FOOTER — plan du site structuré (modèle index3CORANNDIAMSYL.html) :
 *  Marque + mission · colonnes Explorer (READ / Académie / Sciences / Vidéos / Espace) · Sites liés
 *  — et au-dessus, la page affiche LES 28 LIVRES (BooksStrip).
 */
import { useApp, type Section, type AcademyLevel } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import { Globe, ExternalLink } from 'lucide-react';

const SITES = [
  { label: 'Seydina Muhammad Editions', url: 'https://seydinamuhammadeditions.com/Medine/' },
  { label: 'Papen Diaw', url: 'https://papendiaw.ndiaye.info/' },
  { label: 'Président Général Docteur El Hadji Ndongo', url: 'https://presidentgeneraldocteurelhadjindongo.ndiaye.online/' },
  { label: 'Al-Maoun', url: 'https://www.al-maoun.online' },
  { label: 'Ndiamsyl Mody', url: 'https://ndiamsyl-mody.online/mody/2/' },
];

export default function Footer() {
  const { uiLang, setSection, setAcademyLevel } = useApp();
  const t = DICTS[uiLang];

  const go = (s: Section) => setSection(s);
  const goLevel = (lv: AcademyLevel) => { setAcademyLevel(lv); setSection('academy'); };

  const columns: { title: string; links: { label: string; action: () => void }[] }[] = [
    {
      title: t.nav.read.toUpperCase(),
      links: [
        { label: t.nav.livre, action: () => go('livre') },
        { label: t.nav.sourates, action: () => go('sourates') },
        { label: t.nav.read, action: () => go('read') },
        { label: t.nav.listen, action: () => go('listen') },
      ],
    },
    {
      title: t.nav.academy,
      links: [
        { label: 'Foriji', action: () => goLevel('foriji') },
        { label: 'Digdaum', action: () => goLevel('digdaum') },
        { label: 'Hifz', action: () => goLevel('hifz') },
        { label: 'Kang', action: () => goLevel('kang') },
      ],
    },
    {
      title: t.nav.sciences,
      links: [
        { label: t.nav.sciences, action: () => go('sciences') },
        { label: t.nav.miracles, action: () => go('miracles') },
        { label: t.nav.names, action: () => go('names') },
        { label: t.nav.learn, action: () => go('learn') },
        { label: t.nav.write, action: () => go('write') },
      ],
    },
    {
      title: t.nav.videos,
      links: [
        { label: t.nav.videos, action: () => go('videos') },
        { label: t.nav.conference, action: () => go('conference') },
      ],
    },
    {
      title: t.nav.espace,
      links: [
        { label: t.nav.espace, action: () => go('espace') },
        { label: t.nav.tools, action: () => go('tools') },
        { label: t.nav.qibla, action: () => go('qibla') },
        { label: t.nav.hijri, action: () => go('hijri') },
        { label: t.nav.univers, action: () => go('univers') },
      ],
    },
  ];

  return (
    <footer className="mt-auto border-t border-[rgba(212,175,55,0.3)] bg-[rgba(2,22,17,0.95)] pb-[env(safe-area-inset-bottom)]">
      <div className="mx-auto max-w-7xl px-4 py-10">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <span className="star-ornament block h-9 w-9 bg-gradient-to-br from-[#f7e7b0] via-[#d4af37] to-[#9a7b1e]" aria-hidden />
          <p className="font-serif-royal text-xl font-bold text-gold-gradient">THE QURAN IN 1000 LANGUAGES</p>
          <p className="max-w-xl text-xs leading-relaxed text-[#cfe0cf]/60">{t.footer.mission}</p>
        </div>

        {/* ===== Plan du site ===== */}
        <nav className="mb-8 grid grid-cols-2 gap-6 border-y border-[rgba(212,175,55,0.15)] py-6 sm:grid-cols-3 lg:grid-cols-5" aria-label={t.footer.explore}>
          {columns.map((col) => (
            <div key={col.title}>
              <p className="mb-3 text-[11px] font-bold tracking-[0.25em] text-[#d4af37]/85 uppercase">{col.title}</p>
              <ul className="space-y-1.5">
                {col.links.map((lk) => (
                  <li key={lk.label}>
                    <button
                      onClick={lk.action}
                      className="text-[12px] text-[#cfe0cf]/70 transition-colors hover:text-[#f0d98c]"
                    >
                      {lk.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="mb-8">
          <p className="mb-3 flex items-center justify-center gap-2 text-[11px] font-semibold tracking-[0.25em] text-[#d4af37]/80 uppercase">
            <Globe className="h-3.5 w-3.5" /> {t.footer.sites}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {SITES.map((s) => (
              <a
                key={s.url}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-full border border-[rgba(212,175,55,0.25)] px-3.5 py-1.5 text-[11px] font-medium text-[#cfe0cf]/75 transition-colors hover:border-[rgba(212,175,55,0.6)] hover:text-[#f0d98c]"
              >
                {s.label}
                <ExternalLink className="h-3 w-3" aria-hidden />
              </a>
            ))}
          </div>
        </div>

        <div className="border-t border-[rgba(212,175,55,0.15)] pt-5 text-center">
          <p className="text-[11px] text-[#cfe0cf]/45">
            © {new Date().getFullYear()} THE QURAN IN 1000 LANGUAGES — {t.footer.rights}
          </p>
          <p className="mt-1 text-[10px] text-[#cfe0cf]/30">{t.footer.license}</p>
        </div>
      </div>
    </footer>
  );
}
